import type React from "react";
import { useState } from "react";
import { CheckCircle, Clock, Send, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/EmptyState";
import { useClientContacts } from "../useClients";
import { useClientSurveys, useCreateSurveySend } from "../../survey/useSurvey";
import { SURVEY_RESULTS_READY } from "../../survey/feature-flag";
import type { survey, client } from "@duress/shared";
import { survey as surveyNs } from "@duress/shared";

const SURVEY_QUESTIONS = surveyNs.SURVEY_QUESTIONS;

export function ClientSurveyTab({ clientId }: { clientId: string }): React.JSX.Element {
  if (!SURVEY_RESULTS_READY) {
    return (
      <EmptyState
        icon={Clock}
        title="Coming soon"
        body="Customer surveys aren't available yet — check back soon."
      />
    );
  }
  return <ClientSurveyTabContent clientId={clientId} />;
}

function ClientSurveyTabContent({ clientId }: { clientId: string }): React.JSX.Element {
  const { data: surveys, isLoading } = useClientSurveys(clientId);
  const { data: contacts } = useClientContacts(clientId);
  const [sendOpen, setSendOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Customer Success Survey</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Send the satisfaction survey to key contacts and track responses over time.
          </p>
        </div>
        <Button onClick={() => setSendOpen(true)} className="gap-2">
          <Send className="h-4 w-4" />
          Send Survey
        </Button>
      </div>

      {surveys && surveys.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">No surveys sent yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click <strong>Send Survey</strong> to send the first one.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {(surveys ?? []).map((s) => (
            <SurveySendRow
              key={s.id}
              send={s}
              expanded={expandedId === s.id}
              onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
            />
          ))}
        </div>
      )}

      <SendSurveyDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        clientId={clientId}
        contacts={contacts ?? []}
      />
    </div>
  );
}

function SurveySendRow({
  send,
  expanded,
  onToggle,
}: {
  send: survey.SurveySendWithResponse;
  expanded: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const date = new Date(send.sentAt).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors"
      >
        {send.status === "completed" ? (
          <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
        ) : (
          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            Sent {date} by {send.sentByUserName}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {send.sentToContacts.length > 0
              ? `To: ${send.sentToContacts.map((c) => c.name).join(", ")}`
              : "No contacts with email"}
            {" · "}
            <span
              className={
                send.status === "completed" ? "text-green-600 font-medium" : "text-muted-foreground"
              }
            >
              {send.status === "completed" ? "Responded" : "Awaiting response"}
            </span>
          </p>
        </div>
        {send.response && (
          <span className="text-sm font-semibold tabular-nums shrink-0">
            {send.response.averageScore.toFixed(1)}<span className="text-muted-foreground font-normal">/5</span>
          </span>
        )}
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {expanded && send.response && (
        <ResponseDetail response={send.response} />
      )}
      {expanded && !send.response && (
        <div className="px-4 pb-4 pt-2 border-t border-border">
          <SurveyLinkCopy token={send.token} />
        </div>
      )}
    </div>
  );
}

function SurveyLinkCopy({ token }: { token: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/survey/${token}`;

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">Survey link (share manually or via Resend when configured):</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-muted px-3 py-2 rounded font-mono truncate">{url}</code>
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? "Copied!" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

function ResponseDetail({ response }: { response: survey.SurveyResponse }): React.JSX.Element {
  const completedDate = new Date(response.completedAt).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="border-t border-border px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">
            {response.respondentName ? `Completed by ${response.respondentName}` : "Completed anonymously"}
          </p>
          <p className="text-xs text-muted-foreground">{completedDate}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold">{response.averageScore.toFixed(1)}</p>
          <p className="text-xs text-muted-foreground">avg / 5</p>
        </div>
      </div>

      <div className="space-y-3">
        {SURVEY_QUESTIONS.map((question, i) => {
          const score = response.scores[i];
          if (score === undefined) return null;
          return (
            <div key={i} className="space-y-1">
              <div className="flex items-start justify-between gap-4">
                <p className="text-xs text-muted-foreground flex-1">{question}</p>
                <span className="text-sm font-semibold tabular-nums shrink-0">
                  {score}<span className="text-muted-foreground font-normal text-xs">/5</span>
                </span>
              </div>
              <ScoreBar score={score} />
            </div>
          );
        })}
      </div>

      {response.comments && (
        <div className="rounded-md bg-muted p-3">
          <p className="text-xs font-medium text-muted-foreground mb-1">Comments</p>
          <p className="text-sm whitespace-pre-wrap">{response.comments}</p>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score }: { score: number }): React.JSX.Element {
  const pct = (score / 5) * 100;
  const colour =
    score >= 4 ? "bg-green-500" : score === 3 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div className={`h-full rounded-full ${colour}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function SendSurveyDialog({
  open,
  onOpenChange,
  clientId,
  contacts,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientId: string;
  contacts: client.ClientContact[];
}): React.JSX.Element {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { mutate: send, isPending, error } = useCreateSurveySend(clientId);

  const withEmail = contacts.filter((c) => c.email);

  const toggle = (id: string): void => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSend = (): void => {
    if (selectedIds.length === 0) return;
    send(selectedIds, {
      onSuccess: () => {
        onOpenChange(false);
        setSelectedIds([]);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Send Survey</DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-2">
        {withEmail.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No key contacts have an email address. Add emails to contacts in the Details tab first.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Select which contacts to send the survey link to.
            </p>
            <div className="space-y-2">
              {withEmail.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 cursor-pointer hover:bg-accent/50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={selectedIds.includes(c.id)}
                    onChange={() => toggle(c.id)}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                  </div>
                </label>
              ))}
            </div>

            {error && (
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : "Failed to create survey send"}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={selectedIds.length === 0 || isPending}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {isPending ? "Creating…" : "Create Survey Link"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
